export const AUTH_TOKEN_NAME = "cavalry-token";
export const ROLES = ["USER", "ADMIN"];

export const RECORDING_STATES = {
  CREATED: "CREATED",
  PROCESSING: "PROCESSING",
  READY: "READY",
  ERROR: "ERROR"
};

export const RECORDING_STATES_LABELS = {
  CREATED: "Preparing, please wait, it may take up to 5 minutes",
  PROCESSING: "Processing, please wait...",
  READY: "Ready",
  ERROR: "Error, try re-recording"
};

export const DEFAULT_QUESTIONNAIRE = [
  {
    id: 1,
    text: "Are you legally authorized to work in the United States?",
    type: "yesno",
    correctAnswer: "yes",
    cutting: true,
    minValue: 0,
    maxValue: 10,
  },
  {
    id: 2,
    text: "Work experience in an SDR position (years)",
    type: "numeric",
    correctAnswer: "yes",
    minValue: 2,
    maxValue: 99,
    cutting: true,
  },
  {
    id: 3,
    text: "Work experience selling SaaS (Benefits Software)",
    type: "yesno",
    correctAnswer: "yes",
    cutting: false,
    minValue: 0,
    maxValue: 10,
  }
];

export const getRoutes = ({ jobs = [], companies = [] } = {}) => {
  return [
    { path: '/dashboard', breadcrumb: 'Dashboard' },
    { path: '/account', breadcrumb: 'Account' },
    { path: '/company', breadcrumb: 'Company' },
    { path: '/team', breadcrumb: 'Team' },
    { path: '/settings/billing', breadcrumb: 'Billing & Subscription' },
    { path: '/settings/billing/plan', breadcrumb: 'Change Plan' },
    { path: '/company/:clientId', breadcrumb: ({ match }) => companies.find(x => x.id === match.params.clientId)?.name || 'Company', url: 'none' },
    { path: '/company/:clientId/job', breadcrumb: 'Jobs', url: ({ pathname }) => `/company/${pathname.split("/")[2]}/jobs` },
    {
      path: '/company/:clientId/job/:jobId', breadcrumb: ({ match }) => {
        const company = companies.find(x => x.id === match.params.clientId);
        return company?.jobs?.find(x => x.id === match.params.jobId)?.title || jobs.find(x => x.id === match.params.jobId)?.title || 'Job';
      }
    },
    { path: '/company/:clientId/job/:jobId/intro', breadcrumb: 'Introduction' },
    { path: '/company/:clientId/job/:jobId/sign-in', breadcrumb: 'Sign In' },
    { path: '/company/:clientId/job/:jobId/sign-up', breadcrumb: 'Sign Up' },
    { path: '/company/:clientId/job/:jobId/confirm-email', breadcrumb: 'Confirm Email' },
    { path: '/company/:clientId/job/:jobId/apply-profile', breadcrumb: 'Apply Profile' },
    { path: '/company/:clientId/job/:jobId/questionnaire', breadcrumb: 'Questionnaire' },
    { path: '/company/:clientId/job/:jobId/video-interview', breadcrumb: 'Video Interview' },
    { path: '/jobs', breadcrumb: 'Jobs' },
    { path: '/job', breadcrumb: 'Jobs', url: '/jobs' },
    { path: '/job/:jobId', breadcrumb: ({ match }) => jobs.find(x => x.id === match.params.jobId)?.title || 'Job' },
    { path: '/job/:jobId/candidates', breadcrumb: 'Candidates' },
    { path: '/profile', breadcrumb: 'Profile' },
    { path: '/applications', breadcrumb: 'Applications' },
    { path: '/admin', breadcrumb: 'Admin' },
    { path: '/admin/users', breadcrumb: 'Users' },
    { path: '/admin/companies', breadcrumb: 'Companies' },
    { path: '/sign-in', breadcrumb: 'Sign In' },
    { path: '/sign-up', breadcrumb: 'Sign Up' },
    { path: '/sign-up/:inviteCode', breadcrumb: 'Sign Up' },
    { path: '/confirm-email', breadcrumb: 'Confirm Email' },
    { path: '/reset-password', breadcrumb: 'Reset Password' },
    { path: '/start-company', breadcrumb: 'Start Company' },
  ];
};

export const APPLICATION_STAGES = {
  APPLICATION_RECEIVED: "APPLICATION_RECEIVED",
  REVIEW: "REVIEW",
  INTERVIEWS: "INTERVIEWS",
  BACKGROUND_CHECK: "BACKGROUND_CHECK",
  OFFER_EXTENDED: "OFFER_EXTENDED",
  HIRED: "HIRED",
  REJECTED: "REJECTED"
};

export const APPLICATION_STAGES_LABELS = {
  APPLICATION_RECEIVED: "Application Received",
  REVIEW: "Review",
  INTERVIEWS: "Interviews",
  BACKGROUND_CHECK: "Background Check",
  OFFER_EXTENDED: "Offer Extended",
  HIRED: "Hired",
  REJECTED: "Rejected"
};

export const APPLICATION_STAGES_COLORS = {
  APPLICATION_RECEIVED: "neutral",
  REVIEW: "primary",
  INTERVIEWS: "warning",
  BACKGROUND_CHECK: "info",
  OFFER_EXTENDED: "success",
  HIRED: "success",
  REJECTED: "danger"
};

export const REJECTION_TEMPLATE_FEEDBACK_PLACEHOLDER = "[Tailored Feedback Specific to the Candidate]";

// Team member roles
export const TEAM_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  USER: 'user',
  VIEWER: 'viewer',
};

export const TEAM_ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  user: 'User',
  viewer: 'Viewer',
};

export const TEAM_ROLE_COLORS = {
  owner: 'primary',
  admin: 'success',
  user: 'neutral',
  viewer: 'warning',
};

export const TEAM_ROLE_DESCRIPTIONS = {
  owner: 'Full access including ownership transfer',
  admin: 'Full access except ownership transfer',
  user: 'Can view and edit jobs, but not billing or team',
  viewer: 'Can only view jobs and candidates',
};

export const TEAM_ROLE_PERMISSIONS = {
  owner: ['VIEW_JOBS', 'EDIT_JOBS', 'VIEW_BILLING', 'MANAGE_TEAM', 'TRANSFER_OWNERSHIP'],
  admin: ['VIEW_JOBS', 'EDIT_JOBS', 'VIEW_BILLING', 'MANAGE_TEAM'],
  user: ['VIEW_JOBS', 'EDIT_JOBS'],
  viewer: ['VIEW_JOBS'],
};

export const PERMISSION_LABELS = {
  VIEW_JOBS: 'View jobs and candidates',
  EDIT_JOBS: 'Create and edit jobs',
  VIEW_BILLING: 'View billing information',
  MANAGE_TEAM: 'Manage team members',
  TRANSFER_OWNERSHIP: 'Transfer ownership',
};

export const getRejectionMessageTemplate = ({ candidateName, positionTitle, companyName, userName }) => {
  const positionText = companyName 
    ? `the ${positionTitle} with ${companyName}` 
    : `the ${positionTitle}`;
  
  return `Hi ${candidateName},

Thank you for taking the time to apply for ${positionText}. After careful consideration, we have decided to move forward with another candidate.

We wish you all the best in your job search and future endeavors.

Best regards,
${userName}`;
};